/**
 * Navigation Header Component (September 2025)
 * 
 * Terminal-themed navigation with sign in/sign up buttons
 * Responsive hamburger menu for mobile devices
 */

import { useState } from 'react';
import { SignInButton, SignUpButton, SignedIn, SignedOut, UserButton, useUser, useClerk } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';

interface NavigationProps {}

export function Navigation({}: NavigationProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user: clerkUser } = useUser();
  const clerk = useClerk();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleLogoClick = () => {
    navigate({ to: '/' });
  };

  const handleSignOut = async () => {
    await clerk.signOut();
    navigate({ to: '/' });
  };

  return (
    <nav className="terminal-box border-b-2 border-terminal-orange relative z-50 flex-shrink-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button 
              onClick={handleLogoClick}
              className="terminal-glow hover:text-terminal-orange transition-colors"
            >
              <span className="text-terminal-orange-bright font-terminal text-xl font-bold">
                [KEY PALACE]
              </span>
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <button
              onClick={() => navigate({ to: '/docs' })}
              className="terminal-glow text-terminal-orange-bright font-terminal text-sm font-bold hover:text-terminal-orange transition-colors cursor-pointer"
            >
              [DOCS]
            </button>
            <SignedOut>
              <SignInButton>
                <button className="terminal-glow text-terminal-orange-bright font-terminal text-lg font-bold hover:text-terminal-orange transition-colors cursor-pointer">
                  [SIGN IN]
                </button>
              </SignInButton>
              <SignUpButton>
                <button className="terminal-glow text-terminal-orange-bright font-terminal text-lg font-bold hover:text-terminal-orange transition-colors cursor-pointer">
                  [SIGN UP]
                </button>
              </SignUpButton>
            </SignedOut>

            <SignedIn>
              <button
                onClick={() => navigate({ to: '/app/dashboard' })}
                className="terminal-glow text-terminal-orange-bright font-terminal text-sm font-bold hover:text-terminal-orange transition-colors cursor-pointer"
              >
                [DASHBOARD]
              </button>
              <button
                onClick={() => navigate({ to: '/app/dashboard/webhooks' })}
                className="terminal-glow text-terminal-orange-bright font-terminal text-sm font-bold hover:text-terminal-orange transition-colors cursor-pointer"
              >
                [WEBHOOKS]
              </button>
              <button
                onClick={() => navigate({ to: '/app/subscriptions' })}
                className="terminal-glow text-terminal-orange-bright font-terminal text-sm font-bold hover:text-terminal-orange transition-colors cursor-pointer"
              >
                [SUBSCRIPTIONS]
              </button>
              <button
                onClick={() => navigate({ to: '/app/account' })}
                className="terminal-glow text-terminal-orange-bright font-terminal text-sm font-bold hover:text-terminal-orange transition-colors cursor-pointer"
              >
                [ACCOUNT]
              </button>
              <span className="text-sm text-terminal-orange">
                {clerkUser?.firstName ?? clerkUser?.emailAddresses[0]?.emailAddress ?? 'User'}
              </span>
              <button
                onClick={handleSignOut}
                className="bg-terminal-orange text-black font-terminal text-sm font-bold px-4 py-2 hover:bg-terminal-orange-bright transition-colors cursor-pointer border-2 border-terminal-orange"
              >
                [SIGN OUT]
              </button>
            </SignedIn>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="terminal-button p-2"
              aria-label="Toggle navigation menu"
            >
              <div className="w-6 h-6 flex flex-col justify-center space-y-1">
                <span className={`block h-0.5 w-6 bg-terminal-orange transition-transform duration-300 ${
                  isMenuOpen ? 'rotate-45 translate-y-1.5' : ''
                }`} />
                <span className={`block h-0.5 w-6 bg-terminal-orange transition-opacity duration-300 ${
                  isMenuOpen ? 'opacity-0' : ''
                }`} />
                <span className={`block h-0.5 w-6 bg-terminal-orange transition-transform duration-300 ${
                  isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''
                }`} />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div className={`md:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}>
          <div className="px-2 pt-2 pb-3 space-y-3 border-t border-terminal-orange mt-2">
            <button
              onClick={() => {
                navigate({ to: '/docs' });
                setIsMenuOpen(false);
              }}
              className="terminal-glow text-terminal-orange-bright font-terminal text-sm font-bold hover:text-terminal-orange transition-colors cursor-pointer w-full text-left px-3 py-2"
            >
              [DOCS]
            </button>
            <SignedOut>
              <SignInButton>
                <button 
                  className="terminal-glow text-terminal-orange-bright font-terminal text-lg font-bold hover:text-terminal-orange transition-colors cursor-pointer w-full text-left px-3 py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  [SIGN IN]
                </button>
              </SignInButton>
              <SignUpButton>
                <button 
                  className="terminal-glow text-terminal-orange-bright font-terminal text-lg font-bold hover:text-terminal-orange transition-colors cursor-pointer w-full text-left px-3 py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  [SIGN UP]
                </button>
              </SignUpButton>
            </SignedOut>
            
            <SignedIn>
              <div className="px-3 py-2 space-y-3">
                <button
                  onClick={() => {
                    navigate({ to: '/app/dashboard' });
                    setIsMenuOpen(false);
                  }}
                  className="terminal-glow text-terminal-orange-bright font-terminal text-sm font-bold hover:text-terminal-orange transition-colors cursor-pointer w-full text-left"
                >
                  [DASHBOARD]
                </button>
                <button
                  onClick={() => {
                    navigate({ to: '/app/dashboard/webhooks' });
                    setIsMenuOpen(false);
                  }}
                  className="terminal-glow text-terminal-orange-bright font-terminal text-sm font-bold hover:text-terminal-orange transition-colors cursor-pointer w-full text-left"
                >
                  [WEBHOOKS]
                </button>
                <button
                  onClick={() => {
                    navigate({ to: '/app/subscriptions' });
                    setIsMenuOpen(false);
                  }}
                  className="terminal-glow text-terminal-orange-bright font-terminal text-sm font-bold hover:text-terminal-orange transition-colors cursor-pointer w-full text-left"
                >
                  [SUBSCRIPTIONS]
                </button>
                <button
                  onClick={() => {
                    navigate({ to: '/app/account' });
                    setIsMenuOpen(false);
                  }}
                  className="terminal-glow text-terminal-orange-bright font-terminal text-sm font-bold hover:text-terminal-orange transition-colors cursor-pointer w-full text-left"
                >
                  [ACCOUNT]
                </button>
                <span className="block text-sm text-terminal-orange pt-2">
                  {clerkUser?.firstName ?? clerkUser?.emailAddresses[0]?.emailAddress ?? 'User'}
                </span>
                <button
                  onClick={() => {
                    handleSignOut();
                    setIsMenuOpen(false);
                  }}
                  className="bg-terminal-orange text-black font-terminal text-sm font-bold px-4 py-2 hover:bg-terminal-orange-bright transition-colors cursor-pointer border-2 border-terminal-orange w-full"
                >
                  [SIGN OUT]
                </button>
              </div>
            </SignedIn>
          </div>
        </div>
      </div>
    </nav>
  );
}